import type {
  AdminSummaryQuery,
  AdminSummaryResponse,
  AdminTimeseriesResponse,
} from "@codway-dev/shared";
import { useCallback, useEffect, useReducer, useState } from "react";
import { AnalyticsService } from "../services/analytics.service";

export type AnalyticsDateRange = Pick<
  AdminSummaryQuery,
  "date_from" | "date_to"
>;

function isoDateUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Rango inicial al abrir la página: últimos 30 días (inclusive), en UTC. */
export function defaultAnalyticsDateRange(): AnalyticsDateRange {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setUTCDate(dateFrom.getUTCDate() - 29);
  return {
    date_from: isoDateUtc(dateFrom),
    date_to: isoDateUtc(dateTo),
  };
}

export type AnalyticsFetchState = {
  loading: boolean;
  error: string | null;
  summary: AdminSummaryResponse | null;
  timeseries: AdminTimeseriesResponse | null;
};

const initialFetchState: AnalyticsFetchState = {
  loading: true,
  error: null,
  summary: null,
  timeseries: null,
};

type FetchAction =
  | { type: "FETCH_START" }
  | {
      type: "FETCH_SUCCESS";
      summary: AdminSummaryResponse;
      timeseries: AdminTimeseriesResponse;
    }
  | { type: "FETCH_ERROR"; error: string };

function fetchReducer(
  state: AnalyticsFetchState,
  action: FetchAction
): AnalyticsFetchState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return {
        loading: false,
        error: null,
        summary: action.summary,
        timeseries: action.timeseries,
      };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

export type UseAnalyticsDashboardResult = {
  dateRange: AnalyticsDateRange;
  setDateRange: (range: AnalyticsDateRange) => void;
  summary: AdminSummaryResponse | null;
  timeseries: AdminTimeseriesResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useAnalyticsDashboard(): UseAnalyticsDashboardResult {
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>(
    defaultAnalyticsDateRange
  );
  const [fetchState, dispatch] = useReducer(fetchReducer, initialFetchState);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    async function load() {
      dispatch({ type: "FETCH_START" });

      try {
        const [summaryResult, timeseriesResult] = await Promise.all([
          AnalyticsService.getSummary(dateRange, signal),
          AnalyticsService.getTimeseries(dateRange, signal),
        ]);

        dispatch({
          type: "FETCH_SUCCESS",
          summary: summaryResult,
          timeseries: timeseriesResult,
        });
      } catch (err) {
        if (isAbortError(err)) return;
        dispatch({
          type: "FETCH_ERROR",
          error:
            err instanceof Error
              ? err.message
              : "No se pudieron cargar las analíticas",
        });
      }
    }

    void load();

    return () => {
      abortController.abort();
    };
  }, [dateRange, reloadToken]);

  const { summary, timeseries, loading, error } = fetchState;

  return {
    dateRange,
    setDateRange,
    summary,
    timeseries,
    loading,
    error,
    reload,
  };
}
